import * as Preact from "preact"
import Objdict from "objdict"
import "@frog/source/views/dev/DevScriptEditorScreen.less"
import App from "@frog/source/models/App.js"
import Dev from "@frog/source/models/dev/Dev.js"

import Navigation from "@frog/source/engine/navigating/Navigation.js"
import * as Ribbit from "@frog/source/utility/integration/Ribbit.js"
import Random from "@frog/source/utility/Random.js"
const random = new Random()

import games from "@frog/source/data/games"
import unitJobs from "@frog/source/data/unitJobs.js"

import Camera from "@frog/source/views/components/Camera.jsx"
import Cursor from "@frog/source/views/components/Cursor.jsx"
import UnitSprite from "@frog/source/views/components/UnitSprite.jsx"
import DialogueBox from "@frog/source/views/components/DialogueBox.jsx"
import EncounterArea from "@frog/source/views/components/EncounterArea.jsx"
import EditorModeSplash from "@frog/source/views/components/EditorModeSplash.jsx"

// Based on Viridis from Matplotlib
const YELLOW_BLUE_GRADIENT = ["#fde725","#addc30","#5ec962","#28ae80","#21918c","#2c728e","#3b528b","#472d7b","#440154"]

export default class DevScriptEditorScreen {
    render() {
        if(App.game == undefined) {
            const game = games[Object.keys(games)[0]]
            return (
                <div class="DevBattleEditorScreen">
                    <div class="Header">Scripts</div>
                    <span onClick={() => Navigation.go("/games/" + game.slug + "/chapters/" + (game.firstChapterKey || "1"))}>Please load a game</span>
                </div>
            )
        }
        const tabTitle = "Editor: " + App.game.chapter.slug.toUpperCase()
        let stagedDialogue = {}
        if(Dev.devSelection.selectedSceneStep != undefined) {
            const body = Dev.devSelection.selectedSceneStep.body
            const cue = Ribbit.mapKeywordToUnitKey(body.split(":")[0], Dev.loadedProtostage)
            const dialog = body.split(":").slice(1).join(":").split("#")[0]
            stagedDialogue = {
                "units": App.game.chapter.stage.units,
                "dialogue": {
                    "text": dialog,
                    "data": {
                        "speakerKey": cue,
                    },
                },
            }
        }

        return (
            <div class="DevScriptEditorScreen"
                onMouseDown={(event) => App.game.chapter.selection.start()}
                onMouseUp={(event) => App.game.chapter.selection.finish()}
                onWheel={(event) => Dev.devSelection.wheel(event)}
                onDblClick={() => {App.game.chapter.selection.doubleStart()}}
                onContextMenu={(event) => App.game.chapter.selection.cancel()}>
                <title>{tabTitle}</title>
                <DialogueBox stage={stagedDialogue}/>
                <EditorModeSplash text="Scripts Editor"/>
                <Camera chapter={App.game.chapter}>
                    <Cursor stage={Dev.loadedProtostage}/>
                    <WorldImage stage={Dev.loadedProtostage}/>
                    <EncounterAreas zones={Dev.loadedProtostage.world.zones}/>
                    <Units stage={Dev.loadedProtostage}/>
                    {Object.values(Dev.loadedProtostage.world.spots).map((spot) => {
                        if(Dev.tagColors[spot.tag] == undefined) {
                            const tagCount = Object.keys(Dev.tagColors).length
                            Dev.tagColors[spot.tag] = YELLOW_BLUE_GRADIENT[(2 * tagCount) % 9]
                        }
                        return <Spot spot={spot} color={Dev.tagColors[spot.tag]}/>
                    })}
                </Camera>

            </div>
        )
    }
}

class WorldImage {
    render() {
        if(this.props.stage.images != undefined) {
            return this.props.stage.images.map((image, index) => {
                return (
                    <div class="WorldImage" style={{
                        "background-image": "url(" + (image.url || image) + ")",
                        "width": this.props.stage.world.width + "em",
                        "height": this.props.stage.world.height + "em",
                        "z-index": 1 + (index * 10),
                        "filter": "grayscale(1.0)",
                        "mix-blend-mode": image.effect,
                    }}/>
                )
            })
        }
        return (
            <div class="WorldImage" style={{
                "background-image": "url(" + this.props.stage.image + ")",
                "width": this.props.stage.world.width + "em",
                "height": this.props.stage.world.height + "em",
                "filter": "grayscale(1.0)",
            }}>
                {this.encounters}
            </div>
        )
    }
}

class EncounterAreas {
    render() {
        return (
            <div class="EncounterAreas">
                {Object.values(this.props.zones).map((zone) => {
                    return <EncounterArea encounter={zone}/>
                })}
            </div>
        )
    }
}

class Units {
    render() {
        const stage = this.props.stage
        return Object.values(stage.units).map((unit, index) => {
            if(unit == undefined) return

            if(unit.health == undefined) {
                const player = App.game.player
                let partyUnit = player?.party?.units[unit.key] || {}
                unit = Objdict.merge(partyUnit, unit)

                let unitJob = {}
                if(unit.randomJobKey != undefined) {
                    const jobKeys = []
                    unit.randomJobKey.split(",").forEach((jobKey) => {
                        jobKey = jobKey.trim()
                        jobKeys.push(jobKey)
                    })
                    unit.jobKey = random.elementFromSeededKey(jobKeys, unit.key)
                }
                if(unit.jobKey != undefined) {
                    unitJob = unitJobs[App.game.slug + "/" + App.game.chapter.slug + "/" + unit.jobKey]
                        || unitJobs[App.game.slug + "/" + unit.jobKey]
                        || unitJobs[unit.jobKey]
                    if(unitJob == undefined) {
                        console.log("Could not find unit class", unit.jobKey, unit)
                        unitJob = {}
                    }
                }

                unit = Objdict.merge(unitJob, unit)
                if(unit.randomJobKey != undefined) unit.jobKey = undefined
            }

            const roundedPosition = {
                "x": Math.round(unit.position.x),
                "y": Math.round(unit.position.y),
                "z": Math.round(unit.position.z)
            }

            const style = {
                "left": unit.position.x + "em",
                "top": unit.position.y + "em",
                "z-index": 100,
            }
            return (
                <div class="Unit" style={style}>
                    <UnitSprite unit={unit}/>
                </div>
            )
        })
    }
}

class Spot {
    render() {
        return (
            <div class="Spot" style={{
                "left": this.props.spot.position.x + "em",
                "top": this.props.spot.position.y + "em",
                "background-color": this.props.color,
            }}>
                <div class="Selection" isHovered={this.isHovered} isSelected={this.isSelected}/>
            </div>
        )
    }
    get isSelected() {
        return this.props.spot?.key ==  Dev.devSelection.selectedSpotKey
    }
    get isHovered() {
        return this.props.spot?.key == Dev.devSelection.hoveredSpotKey
            || this.props.spot?.key == Dev.devSelection.listHoveredSpotKey
    }
}
